import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import ExportData from './ExportData';
import { prepareExportData } from '../utils/exportUtils';
import { normalizeUsers } from '../utils/userUtils';
import '../styles/ExportPages.css';
import '../styles/ExportComponents.css';
import LoadingSpinner from './LoadingSpinner';

const ExportUsers = ({ userType }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchUsers();
  }, [userType]);

  const fetchUsers = async () => {
    setLoading(true);
    setError("");
    try {
      let endpoint = "";
      switch (userType) {
        case "students":
          endpoint = "/crud/get_all_students";
          break;
        case "preceptors":
          endpoint = "/crud/get_all_preceptors";
          break;
        case "admins":
          endpoint = "/crud/get_all_standalone_admins";
          break;
        default:
          setError("Invalid user type");
          return;
      }

      const response = await axiosInstance.get(endpoint);
      setUsers(normalizeUsers(response.data || []));
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setError("Failed to load users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportSuccess = (_result) => {};

  const handleExportError = (result) => {
    console.error('Export failed:', result);
  };

  // Prepare export data
  const exportDataConfig = prepareExportData(
    users,
    [
      { key: 'firstname', label: 'First Name' },
      { key: 'lastname', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'rank', label: 'Rank' },
      { key: 'speciality', label: 'Speciality' },
      { key: 'institution', label: 'Institution' }
    ],
    `${userType.charAt(0).toUpperCase() + userType.slice(1)} Data Export`,
    `${userType}_export_${new Date().toISOString().split('T')[0]}`,
    null // visibleColumns - use all columns
  );

  if (loading) {
    return (
      <div className="export-page-container">
        <div className="export-loading">
          <LoadingSpinner text={`Loading ${userType} data...`} variant="inline" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="export-page-container">
        <div className="export-error">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem' 
      }}>
        <div>
          <h2 style={{ 
            margin: 0, 
            fontWeight: 700, 
            color: '#2b4663', 
            letterSpacing: 1 
          }}>
            Export {userType.charAt(0).toUpperCase() + userType.slice(1)} Data
          </h2>
          <p style={{ 
            margin: '8px 0 0 0', 
            color: '#666', 
            fontSize: 16 
          }}>
            Export {users.length} {userType} records
          </p>
        </div>
        <ExportData
          {...exportDataConfig}
          onSuccess={handleExportSuccess}
          onError={handleExportError}
          disabled={loading || users.length === 0}
          buttonText={`Export ${userType.charAt(0).toUpperCase() + userType.slice(1)}s`}
          layout="horizontal"
        />
      </div>

      {users.length > 0 && (
        <div style={{ 
          background: '#fff', 
          borderRadius: 16, 
          boxShadow: '0 2px 8px rgba(43,70,99,0.06)', 
          padding: 24 
        }}>
          <h3 style={{ 
            color: '#2b4663', 
            fontWeight: 700, 
            marginTop: 0, 
            marginBottom: 20 
          }}>
            Preview ({users.length} records)
          </h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'separate', 
              borderSpacing: 0,
              borderRadius: 12,
              overflow: 'hidden',
              background: '#fff',
              minWidth: '600px'
            }}>
              <thead>
                <tr style={{ background: '#f4f4f4' }}>
                  <th style={{ padding: 12, borderBottom: '1px solid #e0e0e0', color: '#2b4663', fontWeight: 600 }}>Name</th>
                  <th style={{ padding: 12, borderBottom: '1px solid #e0e0e0', color: '#2b4663', fontWeight: 600 }}>Email</th>
                  <th style={{ padding: 12, borderBottom: '1px solid #e0e0e0', color: '#2b4663', fontWeight: 600 }}>Phone</th>
                  {userType === 'students' && (
                    <th style={{ padding: 12, borderBottom: '1px solid #e0e0e0', color: '#2b4663', fontWeight: 600 }}>Rank</th>
                  )}
                  {userType === 'preceptors' && (
                    <th style={{ padding: 12, borderBottom: '1px solid #e0e0e0', color: '#2b4663', fontWeight: 600 }}>Rank</th>
                  )}
                  {userType === 'admins' && (
                    <th style={{ padding: 12, borderBottom: '1px solid #e0e0e0', color: '#2b4663', fontWeight: 600 }}>Type</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {users.slice(0, 10).map((user, index) => (
                  <tr key={index} style={{ 
                    cursor: 'pointer', 
                    transition: 'background 0.18s' 
                  }} 
                  onMouseEnter={(e) => e.target.parentElement.style.background = '#f8f9fa'} 
                  onMouseLeave={(e) => e.target.parentElement.style.background = 'transparent'}>
                    <td style={{ padding: 12 }}>
                      {user.firstname} {user.lastname}
                    </td>
                    <td style={{ padding: 12 }}>{user.email}</td>
                    <td style={{ padding: 12 }}>{user.phone || 'N/A'}</td>
                    {userType === 'students' && (
                      <td style={{ padding: 12 }}>{user.rank || 'N/A'}</td>
                    )}
                    {userType === 'preceptors' && (
                      <td style={{ padding: 12 }}>{user.rank || 'N/A'}</td>
                    )}
                    {userType === 'admins' && (
                      <td style={{ padding: 12 }}>Standalone Admin</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {users.length > 10 && (
            <p style={{ 
              textAlign: 'center', 
              marginTop: 16, 
              color: '#666', 
              fontStyle: 'italic' 
            }}>
              Showing first 10 of {users.length} records. Use export to see all data.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ExportUsers; 